import { AxiosError, InternalAxiosRequestConfig } from "axios";
import { axiosInstance } from "./request.service";
import Cookies from "js-cookie";
import { COOKIES_NAME } from "constants/appConstants";
import { AuthTokens, refresh } from "services/auth";
import { servicesUtils } from "../utils/services.utils";
import { store } from "../../store/store";
import { logout } from "../../store/slices/authSlice";

export interface BackendErrores {
  [key: string]: { msg: string };
}

export interface BackendError {
  errores: BackendErrores;
  message: string;
  status: number;
}

const defaultBackendError: BackendError = {
  errores: {},
  message: "Error inesperado",
  status: 500,
};

let isRefreshing = false;
let failedQueue: any[] = [];
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });

  failedQueue = [];
};

export const AxiosInterceptor = () => {
  const updateHeader = (request: any) => {
    //const rawTokens = Cookies.get(COOKIES_NAME);
    const rawTokens = sessionStorage.getItem(COOKIES_NAME);

    if (rawTokens) {
      const tokens: AuthTokens = JSON.parse(rawTokens);
      const newHeaders = {
        Authorization: `Bearer ${tokens.accessToken}`,
        withCredentials: "true",
      };
      request.headers = request.headers
        ? { ...request.headers, ...newHeaders }
        : newHeaders;
    }
    return request;
  };

  const errorHandler = async (error: AxiosError<BackendError>) => {
    const errorMessage = error.response
      ? error.response.data.message
      : defaultBackendError.message;
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
      // recuperar refresh token de cookies
      //const rawTokens = Cookies.get(COOKIES_NAME);
      const rawTokens = sessionStorage.getItem(COOKIES_NAME);

      // Si ya no hay tokens (p. ej. el usuario cerró sesión y alguna petición
      // quedó en vuelo), evitamos romper con JSON.parse("") y salimos limpio.
      if (!rawTokens) {
        store.dispatch(logout());
        return Promise.reject(defaultBackendError);
      }

      const tokens: AuthTokens = JSON.parse(rawTokens);
      const isValid = servicesUtils.isTokenValid(tokens.refreshToken);
      if (!isValid) {
        store.dispatch(logout());
        return Promise.reject(defaultBackendError);
      }

      // Tomamos el "lock": solo esta petición refresca; las demás 401 que lleguen
      // mientras tanto se encolan (arriba) y se reintentan con el token nuevo.
      // Esto es clave porque el refresh rota el hash en el backend: si varias
      // refrescaran en paralelo con el mismo refresh token, solo la primera
      // funcionaría y el resto recibiría 401.
      isRefreshing = true;
      try {
        const { accessToken, refreshToken } = await refresh(
          tokens.refreshToken,
        );
        sessionStorage.setItem(
          COOKIES_NAME,
          JSON.stringify({ accessToken, refreshToken }),
        );
        /* Cookies.set(
          COOKIES_NAME,
          JSON.stringify({ accessToken, refreshToken }),
        ); */
        processQueue(null, accessToken);
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (err) {
        // Falló el refresh (refresh token vencido/revocado): rechazamos las
        // peticiones encoladas y cerramos sesión.
        processQueue(err, null);
        store.dispatch(logout());
        return Promise.reject(defaultBackendError);
      } finally {
        isRefreshing = false;
      }
    } else {
      const errorResponse = error.response
        ? error.response.data
        : defaultBackendError;
      return Promise.reject<BackendError>(errorResponse);
    }
  };

  const responseHandler = (response: any) => {
    return response.data;
  };

  //Request salientes
  axiosInstance.interceptors.request.use((request: any) =>
    updateHeader(request),
  );

  //Respone Entrante
  axiosInstance.interceptors.response.use(
    (response) => responseHandler(response),
    (error) => errorHandler(error),
  );
};
